import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody } from "reactstrap";
import { useSelector } from "react-redux";
import { DF_OT0024Context } from "./context";
import GridHeader from "./Grid/GridHeader";
import GridBody from "./Grid/GridBody";
import BizinBackend from "../../../helpers/bizin_helper";
import useGetCsrfToken from "../../../Hooks/useGetCsrfToken";

const PROC_NAME_SAVE = "SRM_Agendamento_Gerenciador_Limite_Obter_Lista_Atualizar";

// Extrai o valor bruto caso a célula venha encapsulada em objeto
const extractRawValue = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "object") {
    return extractRawValue(val.valor ?? val.descricao ?? val.value);
  }
  return val;
};

// Converte valores formatados em número float
const parseNumber = (val) => {
  const cleanVal = extractRawValue(val);
  if (cleanVal === null || cleanVal === undefined || cleanVal === "") return 0;
  if (typeof cleanVal === "number") return cleanVal;

  let clean = String(cleanVal).replace(/[^\d,. -]/g, "").trim();
  if (clean.includes(",") && clean.includes(".")) {
    clean = clean.replace(/\./g, "").replace(",", ".");
  } else if (clean.includes(",")) {
    clean = clean.replace(",", ".");
  }
  return parseFloat(clean) || 0;
};

// Extrai apenas dígitos (ex: "60 Min" -> "60")
const parseOnlyDigits = (val) => {
  const cleanVal = extractRawValue(val);
  if (cleanVal === null || cleanVal === undefined) return "";
  if (typeof cleanVal === "number") return String(cleanVal);
  return String(cleanVal).replace(/\D/g, "");
};

// Verifica se a célula representa 'Ativo' / 'Verdadeiro' ('S')
const checkIsActive = (val) => {
  if (val === null || val === undefined) return false;
  if (typeof val === "boolean") return val;
  if (typeof val === "number") return val === 1;

  if (typeof val === "object") {
    // Objetos do tipo { tipo: "icone", valor: "<classe css>" } representam
    // um dia/status "ativo" quando o transformRow do DF_OT0022 gerou o ícone
    // de check (diaParaIcone/statusParaIcone) — checar isso ANTES de "valor",
    // pois "valor" nesse caso é a classe CSS, não o dado bruto "X"/"S".
    if (val.tipo === "icone") {
      const iconeName = String(val.icone || val.icon || val.className || val.valor || "").toLowerCase();
      if (iconeName.includes("times") || iconeName.includes("close") || iconeName.includes("danger") || iconeName.includes("red")) {
        return false;
      }
      return true;
    }
    if (val.tipo === "texto") return false;
    if (val.valor !== undefined && val.valor !== null) return checkIsActive(val.valor);
    if (val.descricao !== undefined && val.descricao !== null) return checkIsActive(val.descricao);
    if (val.status !== undefined && val.status !== null) return checkIsActive(val.status);
    return false;
  }

  const str = String(val).trim().toUpperCase();
  return str === "S" || str === "X" || str === "SIM" || str === "ATIVO" || str === "A" || str === "1" || str === "TRUE";
};

// Extrai o estado do dia da semana
const getDayValue = (data, key1, key2) => {
  if (!data) return false;
  const raw = data[key1] ?? data[key1?.toLowerCase()] ?? data[key2] ?? data[key2?.toLowerCase()];
  return checkIsActive(raw);
};

export default function DF_OT0024({ isOpen = false, toggle = () => {}, selectedRowData, onSuccess }) {
  console.log("🤖 %cTransação [DF_OT0024]", "color:orange");

  const { userProfile } = useSelector((state) => ({
    userProfile: state.login.profile,
  }));
  const user = useSelector((state) => state.login.user);
  const csrfToken = useGetCsrfToken();

  const [isLoading, setisLoading] = useState(false);
  const [formData, setFormData] = useState({
    limiteDia: "",
    limiteSemana: "",
    limiteMes: "",
    tempoMedioDescarga: "",
    exclusividade: "Não",
    status: "Ativo",
    segunda: false,
    terca: false,
    quarta: false,
    quinta: false,
    sexta: false,
    sabado: false,
    domingo: false,
  });

  useEffect(() => {
    if (isOpen && selectedRowData) {
      const isStatusAtivo = checkIsActive(selectedRowData.Status ?? selectedRowData.status);
      const isExclusivo   = checkIsActive(selectedRowData.Exclusividade ?? selectedRowData.exclusividade);

      setFormData({
        limiteDia:          extractRawValue(selectedRowData.Limite_Financeiro_Diario  || selectedRowData.limiteDia),
        limiteSemana:       extractRawValue(selectedRowData.Limite_Financeiro_Semanal || selectedRowData.limiteSemana),
        limiteMes:          extractRawValue(selectedRowData.Limite_Financeiro_Mensal  || selectedRowData.limiteMes),
        tempoMedioDescarga: parseOnlyDigits(selectedRowData.Tempo_Medio_Descarga || selectedRowData.tempoMedioDescarga),
        exclusividade:      isExclusivo ? "Sim" : "Não",
        status:             isStatusAtivo ? "Ativo" : "Desativado",
        segunda: getDayValue(selectedRowData, "Segunda", "Seg"),
        terca:   getDayValue(selectedRowData, "Terca", "Ter") || getDayValue(selectedRowData, "Terça", "Ter"),
        quarta:  getDayValue(selectedRowData, "Quarta", "Qua"),
        quinta:  getDayValue(selectedRowData, "Quinta", "Qui"),
        sexta:   getDayValue(selectedRowData, "Sexta", "Sex"),
        sabado:  getDayValue(selectedRowData, "Sabado", "Sab") || getDayValue(selectedRowData, "Sábado", "Sab"),
        domingo: getDayValue(selectedRowData, "Domingo", "Dom"),
      });
    }
  }, [isOpen, selectedRowData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setisLoading(true);

      const idParceiro = selectedRowData?.ID_Parametro_Parceiro;
      const idUsuario = userProfile?.ID_Usuario;

      if (!idParceiro) {
        alert("Atenção: Não foi possível identificar o registro selecionado.");
        setisLoading(false);
        return;
      }

      if (!idUsuario) {
        alert("Atenção: Não foi possível identificar o usuário logado.");
        setisLoading(false);
        return;
      }

      const procParams = {
        1:  idParceiro,
        2:  parseNumber(formData.limiteDia),
        3:  parseNumber(formData.limiteSemana),
        4:  parseNumber(formData.limiteMes),
        5:  parseInt(parseNumber(formData.tempoMedioDescarga), 10),
        6:  formData.status === "Ativo" ? "S" : "N",
        7:  formData.segunda ? "X" : null,
        8:  formData.terca   ? "X" : null,
        9:  formData.quarta  ? "X" : null,
        10: formData.quinta  ? "X" : null,
        11: formData.sexta   ? "X" : null,
        12: formData.sabado  ? "X" : null,
        13: formData.domingo ? "X" : null,
        14: formData.exclusividade === "Sim" ? "S" : "N",
        15: idUsuario,
      };

      await BizinBackend.executeProcedure(
        procParams,
        PROC_NAME_SAVE,
        csrfToken,
        user?.access_token
      );

      if (onSuccess) onSuccess();
      if (toggle) toggle();
    } catch (error) {
      console.error("❌ Erro ao salvar atualização de limites:", error);
    } finally {
      setisLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
      <DF_OT0024Context.Provider
        value={{
          isOpen,
          toggle,
          isLoading,
          setisLoading,
          formData,
          handleInputChange,
          selectedRowData,
          handleSave,
        }}
      >
        <ModalBody className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <GridHeader />
            <button
              type="button"
              className="btn border-0 p-0"
              onClick={toggle}
              aria-label="Close"
            >
              <i className="fas fa-times font-size-24 text-gray-secundary"></i>
            </button>
          </div>

          <GridBody />
        </ModalBody>
      </DF_OT0024Context.Provider>
    </Modal>
  );
}